import { useContext } from 'react';
import { v4 as uuid } from 'uuid';
import {
  HeroConversation,
  Product,
  ProductMessageData,
  UploadedFile,
  chatActions,
  useAppDispatch,
  useSendMediaMessage,
  useSendProductMessage,
  useSendTextMessage,
  AuthenticationContext,
} from '@vs-online-only-app/state';
import {
  createLocalMediaMessage,
  createLocalProductMessage,
  createLocalTextMessage,
} from '@vs-online-only-app/ui';
import { useSendContactEmail, MediaItem} from './useSendContactEmail';
import { useUploadEmailMedia } from './useUploadEmailMedia';

const useSendConversationMessages = (conversation?: HeroConversation) => {
  const dispatch = useAppDispatch();
  const { user } = useContext(AuthenticationContext);
  const sendTextMessageMutation = useSendTextMessage(conversation);
  const sendMediaMessageMutation = useSendMediaMessage(conversation);
  const sendProductMessageMutation = useSendProductMessage(conversation);
  const sendContactEmailMutation = useSendContactEmail();
  const uploadEmailMediaMutation = useUploadEmailMedia();

  const sendMessages = async ({
    textMessageBody,
    files,
    sharedProducts,
  }: {
    textMessageBody: string;
    files: UploadedFile[];
    sharedProducts: Product[];
  }) => {
    const author = user?.username as string;
    if (!conversation) {
      return;
    }

    if (conversation.deliveryType === 'email') {
      if (!conversation.contactId) {
        console.error('Contact ID is missing for email delivery type.');
        return;
      }

      let uploadedMedia: MediaItem[] = [];
      if (files.length > 0) {
        uploadedMedia = await Promise.all(
          files.map(async (file) => {
            const type = file.type.includes('video') ? 'video' : 'photo';
            const result = await uploadEmailMediaMutation.mutateAsync({ file });
            return {
              cacheUri: uuid(),
              filename: result.filename,
              mediaUrl: result.url,
              contentType: file.type,
              type,
            };
          }),
        );
      }

      const products = sharedProducts.map((p) => p.sku);

      await sendContactEmailMutation.mutateAsync({
        contactId: conversation.contactId,
        payload: {
          text: textMessageBody,
          ...(uploadedMedia.length > 0 && { media: uploadedMedia }),
          ...(products.length > 0 && { products }),
        },
      });
      return;
    }
    if (textMessageBody) {
      const localTextMessage = createLocalTextMessage({
        conversationId: conversation.id,
        author,
        body: textMessageBody,
      });
      dispatch(
        chatActions.addLocalMessage({
          conversationId: conversation.id,
          localMessage: localTextMessage,
        }),
      );
      sendTextMessageMutation.mutateAsync({
        messageBody: textMessageBody,
        localMessageId: localTextMessage.sid,
      });
    }

    if (sharedProducts.length) {
      sharedProducts.forEach((product) => {
        const localProductMessage = createLocalProductMessage({
          conversationId: conversation.id,
          product,
          author,
        });
        dispatch(
          chatActions.addLocalMessage({
            conversationId: conversation.id,
            localMessage: localProductMessage,
          }),
        );
        sendProductMessageMutation.mutateAsync({
          localMessageId: localProductMessage.sid,
          productData: localProductMessage.attributes
            .data as ProductMessageData,
        });
      });
    }

    if (files.length) {
      files.forEach((file) => {
        const localMediaMessage = createLocalMediaMessage({
          conversationId: conversation.id,

          author,
          mediaType: file.type.includes('video') ? 'video' : 'photo',
          mediaUrl: file.url,
        });
        dispatch(
          chatActions.addLocalMessage({
            conversationId: conversation.id,
            localMessage: localMediaMessage,
          }),
        );
        sendMediaMessageMutation.mutateAsync({
          localMessageId: localMediaMessage.sid,
          mediaFile: file,
        });
      });
    }
  };

  return { sendMessages };
};

export default useSendConversationMessages;
